import { Container, Graphics, Text, type FederatedPointerEvent } from 'pixi.js';
import { AudioBus } from '../../audio/AudioBus';
import { Easings, TweenGroup } from '../../core/Tween';
import type { ScratchSymbol } from '../../model/scratch/ScratchTypes';

const FONT_FAMILY = 'system-ui, -apple-system, sans-serif';

// Audio aliases for the scratch UX. Not yet present in any theme bundle —
// AudioBus.play silently no-ops on unknown aliases, so wiring the calls now
// means asset-team commission of these SFX drops in zero-code-change. Brief:
//   scratch:drag    — short paper-on-foil rasp; loop-friendly (~150-300 ms)
//   scratch:reveal  — soft sparkle / ding when the foil fully fades
const AUDIO_DRAG = 'scratch:drag';
const AUDIO_REVEAL = 'scratch:reveal';

/** Threshold (0..1) of cell area that needs to be scratched before the rest
 * of the foil auto-fades. Tuned at 0.55 — feels satisfying without forcing the
 * player to scrub every corner of the cell. */
const REVEAL_THRESHOLD = 0.55;

/** Granularity of the scratched-fraction estimator. Cells are bucketed into
 * a SAMPLE_GRID×SAMPLE_GRID grid and we count unique buckets touched. 10×10 =
 * 100 buckets is plenty of resolution and cheap to track in a Set. */
const SAMPLE_GRID = 10;

/** Radius of each erase circle as a fraction of cell size. ~22% ≈ a fingertip
 * or cursor-sized blot — a single tap clears a meaningful patch (~1 bucket on
 * each axis) so desktop users can finish the cell with a few clicks even when
 * dragging is awkward. */
const ERASE_RADIUS_RATIO = 0.22;

/** Throttle drag-SFX retriggers — prevents the audio engine from getting
 * spammed by every pointermove sample. ~80 ms between plays keeps the rasp
 * feeling continuous without overlap chaos. */
const DRAG_SFX_INTERVAL_MS = 80;
/** Foil alpha drop per fresh-bucket sample. Tuned so 4-5 unique
 * scratch samples bring the foil to a noticeable 0.4-0.5 alpha (the
 * symbol behind starts to read), and ~7 samples take it to the
 * 0.05 floor. The auto-reveal threshold (REVEAL_THRESHOLD = 0.55,
 * = 55 buckets) crosses well before that, so most plays will see the
 * foil fade visibly under the cursor before completeReveal()'s tween
 * lands the rest. */
const FOIL_FADE_PER_SAMPLE = 0.15;
/** Minimum alpha during the manual scratch phase. completeReveal()
 * tweens this down to 0 once REVEAL_THRESHOLD is crossed. */
const FOIL_FADE_FLOOR = 0.05;

export interface ScratchCellOptions {
  size: number;
  /** Index in the parent grid — passed back through {@code onTap}. */
  index: number;
  /** Foil colour for the closed (un-scratched) face. */
  foilColor?: number;
  onTap: (index: number) => void;
}

export interface ScratchCellRevealOptions {
  /** Skip the scratch interaction and reveal the symbol immediately — used
   * by the "auto-reveal all" debug feature. Default {@code false}. */
  instant?: boolean;
}

/**
 * One scratch-card cell. Closed state shows a metallic-foil panel with a
 * "?" hint glyph; the player drags across the cell to "scratch off" the
 * foil — once the cumulative scratched area crosses {@link REVEAL_THRESHOLD}
 * the rest of the foil fades and the symbol underneath is fully exposed.
 *
 * <p><strong>Reveal mechanic.</strong> Layered approach (vs. render-texture
 * + erase blend): the symbol face sits on top of the foil and is masked by a
 * Graphics that starts empty. Each pointermove sample appends a circle to the
 * mask, exposing that patch of the symbol — visually identical to "scratching
 * off the foil" but cheaper than render-texture juggling and works with no
 * renderer reference passed in.
 *
 * <p><strong>Audio.</strong> Plays {@code scratch:drag} on each meaningful
 * pointermove sample (throttled to avoid clipping) and {@code scratch:reveal}
 * when the foil fully fades. The aliases aren't in any theme bundle yet so
 * the calls silently no-op via {@link AudioBus} — see the constants block at
 * the top of this file for the asset-team brief.
 */
export class ScratchCell extends Container {
  private readonly foilFace: Container;
  private readonly foilBg: Graphics;
  private readonly foilMask: Graphics;
  private readonly revealedFace: Container;
  private readonly revealedBg: Graphics;
  private readonly revealedText: Text;
  private readonly scratchMask: Graphics;
  private readonly tweens = new TweenGroup();

  /** The symbol set by {@link reveal}. Drives the text on the revealed face;
   * remains null until the host arms the cell. */
  private armedSymbol: ScratchSymbol | null = null;
  /** True once the foil has fully faded (either via scratch threshold or via
   * an instant programmatic reveal). After this point all input is ignored. */
  private revealed = false;
  /** Tracks the active drag pointer. Pixi gives us pointerdown / pointermove /
   * pointerup events but we only want to record samples while a pointer is
   * actively held down on this cell. */
  private isDragging = false;
  /** Buckets (as {@code "row,col"} strings) the player has already scratched.
   * Size / total buckets gives the scratched fraction; using a Set means
   * repeated dragging over the same area doesn't double-count. */
  private readonly scratchedBuckets = new Set<string>();
  /** Promise resolver for the in-flight {@link reveal} call — fired when the
   * scratch threshold is crossed (or immediately for an instant reveal). */
  private revealResolver: (() => void) | null = null;
  /** Wall-clock timestamp of the last drag-SFX trigger, for throttling. */
  private lastDragSfxMs = 0;

  constructor(public readonly options: ScratchCellOptions) {
    super();
    this.eventMode = 'static';
    this.cursor = 'pointer';

    const foil = options.foilColor ?? 0xc0c8d0;
    const size = options.size;

    // Layer order (bottom → top): revealedFace (symbol, masked by scratchMask)
    // → foilFace (opaque foil + ? glyph, masked by foilMask). Each scratch
    // sample paints a circle into both masks: a positive fill into scratchMask
    // (so the symbol patch becomes visible) and a cut() into foilMask (so the
    // foil patch is removed). The two masks are inverses of each other —
    // foilMask starts as a full rounded rect, scratchMask starts empty.
    // Once the threshold is crossed the foilFace fades out the remaining foil.
    this.revealedFace = new Container();
    this.revealedBg = new Graphics();
    this.revealedFace.addChild(this.revealedBg);
    this.revealedText = new Text({
      text: '',
      style: {
        fontFamily: FONT_FAMILY,
        fontSize: Math.floor(size * 0.5),
        fontWeight: '900',
        fill: 0xffffff,
        align: 'center',
      },
    });
    this.revealedText.anchor.set(0.5);
    this.revealedFace.addChild(this.revealedText);
    this.addChild(this.revealedFace);

    // Mask for the revealed face: starts empty so nothing of the symbol shows
    // through. Each scratch sample appends a circle to it; once an area is
    // covered by the mask, the corresponding chunk of the symbol becomes
    // visible — looks identical to scratching foil off.
    this.scratchMask = new Graphics();
    this.addChild(this.scratchMask);
    this.revealedFace.mask = this.scratchMask;

    this.foilFace = new Container();
    this.foilBg = new Graphics();
    this.foilFace.addChild(this.foilBg);
    this.paintFoil(foil);
    const hint = new Text({
      text: '?',
      style: {
        fontFamily: FONT_FAMILY,
        fontSize: Math.floor(size * 0.5),
        fontWeight: '900',
        fill: 0xffffff,
      },
    });
    hint.anchor.set(0.5);
    hint.alpha = 0.55;
    this.foilFace.addChild(hint);
    this.addChild(this.foilFace);

    // Mask for the foil face: starts as a full rounded rect covering the cell
    // so the foil is initially visible everywhere. Each scratch sample uses
    // Pixi v8's Graphics.cut() to subtract a circular hole from this filled
    // shape; the hole accumulates across cuts (per GraphicsContext source —
    // cut() appends to the prior fill's hole path), so the foil is erased
    // exactly where the symbol is exposed. The mask is added to the cell so
    // it gets transformed alongside everything else, but it is not rendered
    // (Pixi treats mask sources as off-screen).
    this.foilMask = new Graphics();
    this.foilMask
      .roundRect(-size / 2, -size / 2, size, size, size * 0.16)
      .fill({ color: 0xffffff });
    this.addChild(this.foilMask);
    this.foilFace.mask = this.foilMask;

    this.on('pointerdown', this.handlePointerDown);
    this.on('pointermove', this.handlePointerMove);
    this.on('pointerup', this.handlePointerUp);
    this.on('pointerupoutside', this.handlePointerUp);
  }

  /** Arm the cell with its symbol and resolve when the foil has fully faded.
   *
   * <p><strong>Default flow:</strong> The host calls this in response to the
   * cell's {@link ScratchCellOptions.onTap} callback after the user's first
   * pointerdown. The symbol is set and the user keeps dragging to scratch the
   * foil off; the returned promise resolves when the scratch threshold is
   * crossed and the foil fade finishes.
   *
   * <p><strong>Instant flow ({@code options.instant === true}):</strong> Skip
   * the scratch UX entirely — the foil immediately fades and the promise
   * resolves after the fade. Used by the planned "auto-reveal all" debug
   * feature so QA can quickly cycle outcomes without scrubbing every cell.
   */
  reveal(symbol: ScratchSymbol, options?: ScratchCellRevealOptions): Promise<void> {
    if (this.revealed) return Promise.resolve();
    this.armSymbol(symbol);

    if (options?.instant) {
      return this.completeReveal();
    }

    // If the user has already crossed the threshold before reveal() landed
    // (unlikely but possible if there was async work between onTap and the
    // host's reveal call), finish immediately rather than stalling.
    if (this.scratchedFraction() >= REVEAL_THRESHOLD) {
      return this.completeReveal();
    }

    return new Promise<void>((resolve) => {
      this.revealResolver = resolve;
    });
  }

  destroyCell(): void {
    this.tweens.cancelAll();
  }

  private handlePointerDown = (event: FederatedPointerEvent): void => {
    if (this.revealed) return;
    const isFirstTouch = !this.isDragging && this.scratchedBuckets.size === 0;
    this.isDragging = true;
    if (isFirstTouch) {
      // Fire the host callback BEFORE recording the scratch sample — gives
      // the host the opportunity to arm the symbol synchronously so the very
      // first revealed pixel already shows the rolled symbol underneath.
      this.options.onTap(this.options.index);
    }
    this.recordScratchAt(event);
  };

  private handlePointerMove = (event: FederatedPointerEvent): void => {
    if (this.revealed || !this.isDragging) return;
    this.recordScratchAt(event);
  };

  private handlePointerUp = (): void => {
    this.isDragging = false;
  };

  /** Sample the pointer at {@code event}, paint a circle into the mask (so
   * that patch of the symbol shows through), and bump the scratched-fraction
   * estimator. Triggers the auto-fade once the threshold is crossed. */
  private recordScratchAt(event: FederatedPointerEvent): void {
    const local = event.getLocalPosition(this);
    const size = this.options.size;
    const half = size / 2;
    // Reject samples outside the cell — pointerupoutside drags can fire
    // pointermove with local coords past the bounds; clipping keeps the
    // bucket math honest and avoids painting mask circles in the gutter.
    if (local.x < -half || local.x > half || local.y < -half || local.y > half) {
      return;
    }

    const radius = size * ERASE_RADIUS_RATIO;
    this.scratchMask.circle(local.x, local.y, radius).fill({ color: 0xffffff });
    // Cumulatively fade the foil as the player scratches. Pixi v8's
    // Graphics.cut() with a stencil mask didn't visibly remove pixels
    // in our setup (live test showed model.reveal firing but the foil
    // stayed opaque), so we fall back to a continuous alpha fade. The
    // foil's alpha drops by FOIL_FADE_PER_SAMPLE per fresh-bucket
    // sample — the symbol shows through proportionally as the player
    // scratches. Once REVEAL_THRESHOLD is crossed, completeReveal()
    // drives the remaining alpha → 0.
    this.foilMask.circle(local.x, local.y, radius).cut();

    // Bucket the sample location for fraction tracking. Coordinates are in
    // [-half, half], so shift to [0, size] then bucket.
    const col = Math.min(
      SAMPLE_GRID - 1,
      Math.max(0, Math.floor(((local.x + half) / size) * SAMPLE_GRID)),
    );
    const row = Math.min(
      SAMPLE_GRID - 1,
      Math.max(0, Math.floor(((local.y + half) / size) * SAMPLE_GRID)),
    );
    const wasNew = !this.scratchedBuckets.has(`${row},${col}`);
    this.scratchedBuckets.add(`${row},${col}`);

    // Drag SFX — only on a "fresh" bucket to avoid spamming when the player
    // re-scratches the same area; throttled so even rapid drags don't stack.
    if (wasNew) {
      // Fade the foil alpha proportionally with each fresh bucket
      // touched. Floor at 0.05 so a sliver always remains until
      // completeReveal() drives the rest. Without this fall-back the
      // mask cut() above isn't visually removing pixels (Pixi v8
      // stencil-mask quirk), so the player's scratch had no visible
      // effect on the foil layer.
      const fresh = this.scratchedBuckets.size;
      const fade = Math.max(FOIL_FADE_FLOOR, 1 - fresh * FOIL_FADE_PER_SAMPLE);
      this.foilFace.alpha = fade;
      const now = performance.now();
      if (now - this.lastDragSfxMs >= DRAG_SFX_INTERVAL_MS) {
        AudioBus.shared.play(AUDIO_DRAG);
        this.lastDragSfxMs = now;
      }
    }

    if (this.scratchedFraction() >= REVEAL_THRESHOLD && this.revealResolver) {
      const resolve = this.revealResolver;
      this.revealResolver = null;
      void this.completeReveal().then(resolve);
    }
  }

  private scratchedFraction(): number {
    return this.scratchedBuckets.size / (SAMPLE_GRID * SAMPLE_GRID);
  }

  /** Set the symbol that will appear under the foil. Idempotent — calling
   * twice with the same cell is a no-op so the host doesn't have to gate. */
  private armSymbol(symbol: ScratchSymbol): void {
    if (this.armedSymbol === symbol) return;
    this.armedSymbol = symbol;
    this.revealedText.text = symbol.glyph;
    this.paintRevealedBg(symbol.tint);
  }

  /** Fade the foil out and lock the cell. Always returns a fresh promise that
   * resolves once the fade completes — callers can chain post-reveal work
   * onto it without worrying about double-resolution. */
  private completeReveal(): Promise<void> {
    if (this.revealed) return Promise.resolve();
    this.revealed = true;
    this.eventMode = 'auto';
    this.cursor = 'default';
    this.isDragging = false;
    // Drop the mask so the symbol shows through everywhere — the foil's
    // alpha tween handles the visual; the unmasking is what guarantees the
    // final state is fully revealed even if the player only scratched a
    // narrow strip down the middle.
    this.revealedFace.mask = null;

    AudioBus.shared.play(AUDIO_REVEAL);

    return this.tweens.startAsync({
      durationMs: 240,
      easing: Easings.easeOutCubic,
      onUpdate: (p) => {
        this.foilFace.alpha = 1 - p;
      },
      onComplete: () => {
        this.foilFace.alpha = 0;
        this.foilFace.visible = false;
      },
    });
  }

  private paintFoil(foil: number): void {
    const size = this.options.size;
    this.foilBg.clear();
    this.foilBg
      .roundRect(-size / 2, -size / 2, size, size, size * 0.16)
      .fill({ color: foil, alpha: 0.95 })
      .stroke({ color: 0xffffff, alpha: 0.3, width: 2 });
  }

  private paintRevealedBg(tint: number): void {
    const size = this.options.size;
    this.revealedBg.clear();
    this.revealedBg
      .roundRect(-size / 2, -size / 2, size, size, size * 0.16)
      .fill({ color: tint, alpha: 0.95 })
      .stroke({ color: 0xffffff, alpha: 0.55, width: 2 });
  }
}
